import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../components/Modal";
import { useRequest } from "../hooks/useRequest";
import { getPaymentMethods } from "../api/paymentMethods";
import { rechargeCredits } from "../api/credits";
import { getStripe } from "../lib/stripe";
import { formatCents } from "../utils/format";
import "./RechargeModal.scss";

/** 充值档位（美元） */
const TIERS = [50, 100, 200, 500];
const MIN_DOLLARS = 1;

/** 生成 orderNo（幂等键）；randomUUID 不可用时降级 */
function genOrderNo(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `ord-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** 余额展示所需字段（由 Dashboard 传入，避免重复请求） */
export interface RechargeBalance {
  monthlyCreditsCents: number;
  purchasedCreditsCents: number;
  totalBalanceCents: number;
}

const brand = (b: string) => b.charAt(0).toUpperCase() + b.slice(1);

/** 表单本体（Modal 关闭时不挂载，因此每次打开都是全新状态） */
const RechargeForm: React.FC<{
  balance: RechargeBalance | null;
  onSuccess: () => void;
  onClose: () => void;
}> = ({ balance, onSuccess, onClose }) => {
  const navigate = useNavigate();
  const cards = useRequest(getPaymentMethods);

  // 跳转到账户设置添加银行卡（先关闭弹窗）
  const goAddCard = () => {
    onClose();
    navigate("/account-settings");
  };

  const [tier, setTier] = useState<number | null>(50);
  const [custom, setCustom] = useState("");
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  // orderNo：打开时生成一次，切换卡/重试都保持不变
  const [orderNo] = useState<string>(genOrderNo);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  // 默认选中默认卡（无默认则选第一张）
  useEffect(() => {
    if (!cards.data || selectedCardId) return;
    const def = cards.data.find((c) => c.isDefault) || cards.data[0];
    if (def) setSelectedCardId(def.id);
  }, [cards.data, selectedCardId]);

  // 有效金额（美元）：自定义优先
  const dollars = custom.trim() !== "" ? Number(custom) : tier;
  const amountCents = Math.round((dollars || 0) * 100);

  const handleConfirm = async () => {
    setError(null);
    if (!dollars || Number.isNaN(dollars) || dollars < MIN_DOLLARS) {
      setError(`Minimum recharge is $${MIN_DOLLARS}.`);
      return;
    }
    if (!selectedCardId) {
      setError("Please select a payment method.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await rechargeCredits({
        amountCents,
        paymentMethodId: selectedCardId,
        orderNo,
      });

      if (res.status === "succeeded") {
        setSucceeded(true);
        onSuccess();
      } else if (res.status === "requires_action" && res.clientSecret) {
        // 3DS：Stripe.js 自动弹验证框
        const stripe = await getStripe();
        if (!stripe) {
          setError("Payment is not configured.");
          return;
        }
        const { error: sErr, paymentIntent } = await stripe.confirmCardPayment(
          res.clientSecret
        );
        if (sErr) {
          setError(sErr.message || "Payment failed.");
        } else if (paymentIntent?.status === "succeeded") {
          setSucceeded(true);
          onSuccess();
        } else {
          setError("Payment could not be completed. Please try again.");
        }
      } else if (res.status === "requires_payment_method") {
        // 卡被拒：保留同一 orderNo，让用户换卡重试
        setError(res.message || "Card declined. Please try a different card.");
      }
    } catch (e) {
      // 超时/网络错误：保留同一 orderNo，可安全重试
      setError(
        e instanceof Error
          ? `${e.message} You can safely retry.`
          : "Request failed. You can safely retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-recharge">
      <h2 className="cs-recharge__title">Recharge Credits</h2>
      <p className="cs-recharge__balance">
        {balance
          ? `Plan Credits: ${formatCents(
              balance.monthlyCreditsCents
            )} · Purchased: ${formatCents(
              balance.purchasedCreditsCents
            )} · Total: ${formatCents(balance.totalBalanceCents)}`
          : "—"}
      </p>

      {/* 档位 */}
      <div className="cs-recharge__tiers">
        {TIERS.map((t) => (
          <button
            key={t}
            type="button"
            className={`cs-recharge__tier${
              custom.trim() === "" && tier === t
                ? " cs-recharge__tier--active"
                : ""
            }`}
            onClick={() => {
              setTier(t);
              setCustom("");
            }}
          >
            ${t}
          </button>
        ))}
      </div>

      {/* 自定义金额 */}
      <div className="cs-recharge__custom">
        <span className="cs-recharge__custom-label">Custom</span>
        <input
          className="cs-recharge__custom-input"
          type="number"
          min={MIN_DOLLARS}
          placeholder="$ Enter amount"
          value={custom}
          onChange={(e) => {
            setCustom(e.target.value);
            setTier(null);
          }}
        />
      </div>

      {/* 信用卡列表：默认选中默认卡，可切换 */}
      {cards.loading ? (
        <div className="cs-recharge__muted">Loading cards…</div>
      ) : cards.error ? (
        <div className="cs-recharge__error">{cards.error}</div>
      ) : (cards.data || []).length === 0 ? (
        <div className="cs-recharge__muted">
          No payment methods.{" "}
          <button
            type="button"
            className="cs-recharge__add-card"
            onClick={goAddCard}
          >
            Add a card first.
          </button>
        </div>
      ) : (
        <div className="cs-recharge__cards">
          {(cards.data || []).map((c) => (
            <div
              key={c.id}
              className={`cs-recharge__card${
                selectedCardId === c.id ? " cs-recharge__card--active" : ""
              }`}
              onClick={() => setSelectedCardId(c.id)}
            >
              <span>💳</span>
              <span>
                {brand(c.brand)} **** {c.last4}
              </span>
              {c.isDefault && (
                <span className="cs-recharge__card-default">(Default)</span>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="cs-recharge__error">{error}</p>}
      {succeeded && (
        <p className="cs-recharge__success">✓ Recharge successful.</p>
      )}

      {/* 底部按钮 */}
      <div className="cs-recharge__actions">
        <button
          type="button"
          className="cs-recharge__cancel"
          onClick={onClose}
        >
          {succeeded ? "Close" : "Cancel"}
        </button>
        {!succeeded && (
          <button
            type="button"
            className="cs-recharge__primary"
            disabled={submitting}
            onClick={handleConfirm}
          >
            {submitting ? "Processing…" : "Confirm Payment"}
          </button>
        )}
      </div>
    </div>
  );
};

/** 充值弹窗（流程二） */
const RechargeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  balance: RechargeBalance | null;
  onSuccess: () => void;
}> = ({ open, onClose, balance, onSuccess }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <RechargeForm balance={balance} onSuccess={onSuccess} onClose={onClose} />
    </Modal>
  );
};

export default RechargeModal;
